import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { useCurrentUser, userKeys } from '../features/users/users.api';
import { apiRequest, jsonBody } from '../lib/api-client';
import type { CurrentUser } from '../lib/api-types';
import { I18nContext, type I18nValue, type Language, type TranslationParams } from './i18n-context';
import { enMessages, esMessages, type TranslationKey } from './messages';

const STORAGE_KEY = 'financego-language';

function initialLanguage(): Language {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'en' || stored === 'es' ? stored : 'es';
}

export function I18nProvider({ children }: { readonly children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(initialLanguage);
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();

  useEffect(() => {
    const preferred = currentUser.data?.preferences?.language;
    if (preferred) setLanguageState(preferred);
  }, [currentUser.data?.preferences?.language]);

  useEffect(() => {
    document.documentElement.lang = language;
    localStorage.setItem(STORAGE_KEY, language);
    document.title =
      language === 'es' ? 'FinanceGo · Claridad financiera' : 'FinanceGo · Financial clarity';
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    description?.setAttribute(
      'content',
      language === 'es'
        ? 'FinanceGo — claridad financiera para cada día.'
        : 'FinanceGo — financial clarity for every day.',
    );
  }, [language]);

  const setLanguage = useCallback(
    async (nextLanguage: Language) => {
      setLanguageState(nextLanguage);
      const user = currentUser.data;
      if (!user?.preferences) return;
      const response = await apiRequest<{ user: CurrentUser }>('/api/users/preferences', {
        body: jsonBody({ ...user.preferences, language: nextLanguage }),
        method: 'POST',
      });
      queryClient.setQueryData(userKeys.current, response.user);
    },
    [currentUser.data, queryClient],
  );

  const t = useCallback(
    (key: TranslationKey, params: TranslationParams = {}) => {
      const messages = language === 'es' ? esMessages : enMessages;
      return interpolate(messages[key], params);
    },
    [language],
  );

  const value = useMemo<I18nValue>(
    () => ({
      language,
      locale: language === 'es' ? 'es-US' : 'en-US',
      setLanguage,
      t,
    }),
    [language, setLanguage, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function interpolate(message: string, params: TranslationParams): string {
  return message.replaceAll(/\{(\w+)\}/gu, (_match, key: string) => String(params[key] ?? ''));
}
